<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import L from 'leaflet'
import { TILE_LAYERS } from '@/store/mapConstants'

const OSM = TILE_LAYERS.OpenStreetMap

// Vista de todo el territorio: polígono y un marcador numerado por cada manzana.
// Las manzanas ya trabajadas se muestran apagadas. Usa Leaflet directo (sin
// useMapTiles) para no heredar preferencias de la app admin en la vista pública.

const props = defineProps({
  limits: { type: Array, default: () => [] },
  color: { type: Object, default: () => ({}) },
  manzanas: { type: Array, default: () => [] }, // [{ idx, latlng, trabajada }]
  etiqueta: { type: String, default: '' },
})

// Al tocar el número de una manzana se emite su índice (sirve para marcarla).
const emit = defineEmits(['clic-manzana'])

const contenedor = ref(null)
let mapa = null
let grupo = null
let grupoUbicacion = null
let watchId = null

const PRECISION_MAXIMA = 100 // metros

const buscando = ref(false)
const siguiendo = ref(false)
const precision = ref(null)
const errorUbicacion = ref('')

const toLatlng = (v) => (Array.isArray(v) ? [Number(v[0]), Number(v[1])] : [Number(v?.lat), Number(v?.lng)])
const esValido = ([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng)

const colorTerritorio = () => props.color?.backgroundColor || '#1F5C4D'

// `ajustar`: si se reencuadra el mapa. Sólo al cambiar el territorio, para que
// marcar una manzana no mueva la vista.
const dibujar = (ajustar = true) => {
  if (!mapa) return
  grupo.clearLayers()

  const coords = (props.limits || []).map(toLatlng).filter(esValido)
  if (coords.length >= 3) {
    L.polygon(coords, {
      color: colorTerritorio(),
      fillColor: colorTerritorio(),
      fillOpacity: 0.12,
      weight: 2,
    }).addTo(grupo)
  }

  const puntos = props.manzanas
    .map((m) => ({ ...m, ll: toLatlng(m.latlng) }))
    .filter((m) => esValido(m.ll))

  puntos.forEach((m) => {
    L.marker(m.ll, {
      title: `Manzana ${m.idx}`,
      icon: L.divIcon({
        className: '',
        iconSize: [34, 34],
        iconAnchor: [17, 17],
        html: `<div class="mayores-marca-manzana${m.trabajada ? ' mayores-marca-manzana--trabajada' : ''}">${m.idx}</div>`,
      }),
    })
      .on('click', () => emit('clic-manzana', m.idx))
      .addTo(grupo)
  })

  if (!ajustar) return
  const bounds = L.latLngBounds([...coords, ...puntos.map((p) => p.ll)])
  if (bounds.isValid()) mapa.fitBounds(bounds, { padding: [32, 32], maxZoom: 18 })
}

onMounted(() => {
  mapa = L.map(contenedor.value, { attributionControl: true })
  L.tileLayer(OSM.url, {
    attribution: OSM.attribution,
    maxZoom: OSM.maxZoom ?? 19,
    subdomains: OSM.subdomains ?? 'abc',
  }).addTo(mapa)
  grupo = L.layerGroup().addTo(mapa)
  grupoUbicacion = L.layerGroup().addTo(mapa)
  dibujar()
})

watch(() => props.limits, () => dibujar(true), { deep: true })
watch(() => props.manzanas, () => dibujar(false), { deep: true })

// Muestra un punto azul en la posición del dispositivo, con el radio de precisión.
const dibujarUbicacion = (ll, precision) => {
  grupoUbicacion.clearLayers()
  L.circle(ll, { radius: precision, color: '#1E73E8', fillColor: '#1E73E8', fillOpacity: 0.1, weight: 1 }).addTo(grupoUbicacion)
  L.circleMarker(ll, { radius: 9, color: '#fff', weight: 3, fillColor: '#1E73E8', fillOpacity: 1 }).addTo(grupoUbicacion)
}

// Sigue la posición del dispositivo en tiempo real: el punto azul se mueve con
// cada lectura del GPS. El mapa se centra sólo en la primera lectura, para no
// pelear con el movimiento manual del usuario.
const dejarDeSeguir = () => {
  if (watchId !== null) navigator.geolocation.clearWatch(watchId)
  watchId = null
  siguiendo.value = false
  buscando.value = false
  precision.value = null
  grupoUbicacion?.clearLayers()
}

const empezarASeguir = () => {
  if (!navigator.geolocation) {
    errorUbicacion.value = 'Este dispositivo no permite ver la ubicación.'
    return
  }
  buscando.value = true
  siguiendo.value = true
  errorUbicacion.value = ''
  let primeraLectura = true
  watchId = navigator.geolocation.watchPosition(
    ({ coords }) => {
      // Las lecturas con poca precisión son estimaciones de red (antena o Wi‑Fi),
      // no del GPS: no se muestran hasta que llegue una lectura precisa.
      if (coords.accuracy > PRECISION_MAXIMA) return
      buscando.value = false
      precision.value = Math.round(coords.accuracy)
      const ll = [coords.latitude, coords.longitude]
      dibujarUbicacion(ll, coords.accuracy)
      if (primeraLectura) {
        mapa.setView(ll, Math.max(mapa.getZoom(), 17))
        primeraLectura = false
      }
    },
    () => {
      dejarDeSeguir()
      errorUbicacion.value = 'No se pudo obtener tu ubicación. Verificá que el permiso de ubicación esté activado en el navegador.'
    },
    { enableHighAccuracy: true, maximumAge: 0, timeout: 30000 }
  )
}

const alternarUbicacion = () => {
  if (siguiendo.value) dejarDeSeguir()
  else empezarASeguir()
}

onBeforeUnmount(() => {
  dejarDeSeguir()
  mapa?.remove()
  mapa = null
})
</script>

<template>
  <div class="mayores-mapa-completo-wrap">
    <div
      ref="contenedor"
      class="mayores-mapa-completo"
      role="region"
      :aria-label="`Mapa completo del territorio ${etiqueta}`"
    />

    <button
      type="button"
      class="mayores-mapa-completo__ubicacion"
      :class="{ 'mayores-mapa-completo__ubicacion--activo': siguiendo }"
      :aria-label="siguiendo ? 'Dejar de seguir mi ubicación' : 'Mostrar mi ubicación'"
      :aria-pressed="siguiendo"
      :title="siguiendo ? 'Dejar de seguir mi ubicación' : 'Mostrar mi ubicación'"
      @click="alternarUbicacion"
    >
      <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" focusable="false">
        <circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" stroke-width="2" />
        <circle cx="12" cy="12" r="2.5" fill="currentColor" />
        <path d="M12 1v4M12 19v4M1 12h4M19 12h4" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
      </svg>
    </button>

    <p v-if="siguiendo && precision !== null" class="mayores-mapa-completo__precision">
      Precisión: ±{{ precision }} m
    </p>

    <p v-if="errorUbicacion" class="mayores-mapa-completo__aviso" role="alert">
      {{ errorUbicacion }}
    </p>
  </div>
</template>
